import { useState, useMemo, Fragment, useEffect, useRef, useCallback } from 'react';
import type { ReactNode } from 'react';
import { isEmpty, get } from 'lodash';

import { EmptyState, Icon, Pagination } from '@repo/ui';
import { ErrorCard } from './ErrorCard';
import { parseApiErrorUtil } from '../utils/parseApiErrorUtil';

// ============================================================================
// Types and Interfaces
// ============================================================================

export type SortDirection = 'asc' | 'desc';

export interface DataTableColumn<T = any> {
  /** Unique identifier for the column */
  key: string;
  /** Header label */
  header: string | ReactNode;
  /** Alignment */
  align?: 'left' | 'right' | 'center';
  /** Sortable */
  isSortable?: boolean;
  /** Width class */
  width?: string;
  /** Header cell className */
  headerClassName?: string;
  /** Custom header renderer */
  renderHeader?: () => ReactNode;
  /** Cell renderer */
  renderCell: (row: T, index: number) => ReactNode;
  /** Column className */
  className?: string;
  renderFooter?: () => ReactNode;
  /** Enable column resizing */
  isResizable?: boolean;
  /** Default width in pixels */
  defaultWidth?: number;
}

export interface DataTablePagination {
  page: number;
  limit: number;
}

export interface DataTablePaginatedResponse<T> {
  data: T[];
  total: number;
  page?: number;
  limit?: number;
  totals?: any;
  hasMore?: boolean;
  summary?: any;
}

export type RowKeyGenerator<T = any> = (row: T, index: number) => string;

export type ExpandedRowRenderer<T = any> = (row: T, index: number) => ReactNode;

export interface DataTableProps<T = any> {
  /** Table columns */
  columns: DataTableColumn<T>[];
  /** Data fetch function or hook result */
  data?: DataTablePaginatedResponse<T> | null;
  /** Loading state */
  isLoading?: boolean;
  /** Error state */
  error?: Error | null;
  /** Retry callback for error state */
  onRetry?: () => void;
  /** Current pagination */
  pagination?: DataTablePagination;
  /** Pagination change handler */
  onPaginationChange?: (pagination: DataTablePagination) => void;
  /** Empty state props */
  emptyState?: {
    icon?: string;
    title?: string;
    description?: string;
  };
  sorting?: Record<string, SortDirection>;
  /** Table container className */
  tableContainerClassName?: string;
  /** Table className */
  tableClassName?: string;
  /** Enable sticky header */
  stickyHeader?: boolean;
  /** Table max height */
  tableMaxHeight?: string;
  /** Page size options */
  pageSizeOptions?: number[];
  /** Key row */
  rowKey?: string;
  /** Custom header section (filters, summary cards, etc.) */
  renderHeader?: () => ReactNode;
  /** Custom action section (export button, etc.) - rendered at top right of table */
  renderAction?: () => ReactNode;
  /** Hide pagination */
  hidePagination?: boolean;
  /** Row key generator */
  getRowKey?: RowKeyGenerator<T>;
  /** Expanded row content renderer */
  renderExpandedRow?: ExpandedRowRenderer<T>;
  /** Function to determine if row can be expanded */
  isRowExpandable?: (row: T, index: number) => boolean;
  /** Row click handler */
  onRowClick?: (row: T, index: number) => void;
  /** Custom row className */
  getRowClassName?: (row: T, index: number) => string;
  /** Custom onSort function */
  onSort?: (sortBy: Record<string, SortDirection>) => void;
  /** Loading skeleton row count */
  loadingRowCount?: number;
  /** Loading skeleton column count */
  loadingColumnCount?: number;
  renderFooter?: () => ReactNode;
  /** Enable column resizing */
  enableColumnResizing?: boolean;
  /** Storage key for column widths in localStorage */
  columnWidthsStorageKey?: string;
  /** Callback when column width changes */
  onColumnWidthChange?: (columnKey: string, width: number) => void;
}

// ============================================================================
// DataTable Component
// ============================================================================

/**
 * Generic DataTable component for displaying paginated, filterable data
 * Supports expandable rows, custom filters, and more
 * Can be used across the entire application for any table needs
 */
export function DataTable<T = any>({
  columns,
  data,
  isLoading = false,
  error = null,
  onRetry,
  pagination,
  onPaginationChange,
  emptyState = {
    icon: 'HiUsers',
    title: 'No data available',
    description: 'There are no items matching your current filters.',
  },
  tableContainerClassName = '',
  tableClassName = 'min-w-full border-collapse',
  stickyHeader = false,
  tableMaxHeight,
  pageSizeOptions = [25, 50, 100, 200],
  renderHeader,
  renderAction,
  hidePagination = false,
  rowKey = '',
  getRowKey = (row: T, index: number) => {
    if (row && typeof row === 'object') {
      return rowKey
        ? `${get(row, rowKey)}-${index}`
        : (row as any).id ||
            (row as any).invoiceId ||
            (row as any).clientId ||
            (row as any).billId ||
            `row-${index}`;
    }
    return `row-${index}`;
  },
  renderExpandedRow,
  isRowExpandable,
  onRowClick,
  getRowClassName,
  onSort,
  sorting = {},
  loadingRowCount = 5,
  loadingColumnCount = 7,
  renderFooter,
  enableColumnResizing = true,
  columnWidthsStorageKey,
  onColumnWidthChange,
}: DataTableProps<T>) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Column resizing state
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [resizingStorageKey, setResizingStorageKey] = useState<string | undefined>(
    columnWidthsStorageKey
  );
  const resizeStartXRef = useRef<number>(0);
  const resizeStartWidthRef = useRef<number>(0);

  // Generate storage key
  const storageKey = useMemo(() => {
    if (columnWidthsStorageKey) {
      return columnWidthsStorageKey;
    }

    return `datatable-column-widths-${columns.map(c => c.key).join('-')}`;
  }, [columnWidthsStorageKey, columns]);

  const initialColumnsWidth = useCallback(() => {
    if (!storageKey) return;
    setColumnWidths({});
    localStorage.setItem(storageKey, JSON.stringify({}));
  }, [storageKey]);

  useEffect(() => {
    if (resizingStorageKey === storageKey) return;

    setResizingStorageKey(storageKey);
  }, [resizingStorageKey, storageKey]);

  // Load column widths from localStorage on mount
  useEffect(() => {
    if (!enableColumnResizing || !storageKey) return;

    try {
      const savedColumnWidths = JSON.parse(localStorage.getItem(storageKey) || '{}');
      if (isEmpty(savedColumnWidths)) {
        initialColumnsWidth();
        return;
      }

      setColumnWidths(savedColumnWidths);
    } catch (error) {
      console.warn('Failed to load column widths from localStorage:', error);
    }
  }, [enableColumnResizing, initialColumnsWidth, storageKey]);

  // Save column widths to localStorage when they change
  useEffect(() => {
    if (!enableColumnResizing || !storageKey || Object.keys(columnWidths).length === 0) return;

    try {
      const savedColumnWidths = JSON.parse(localStorage.getItem(storageKey) || '{}');
      if (resizingStorageKey !== storageKey) {
        localStorage.setItem(storageKey, JSON.stringify({ ...savedColumnWidths }));
      } else {
        localStorage.setItem(storageKey, JSON.stringify(columnWidths));
      }
    } catch (error) {
      console.warn('Failed to save column widths to localStorage:', error);
    }
  }, [columnWidths, enableColumnResizing, initialColumnsWidth, resizingStorageKey, storageKey]);

  const handleSorting = (colKey: string) => {
    let nextDirection: SortDirection = 'desc';
    if (!isEmpty(sorting) && sorting[colKey]) {
      if (sorting[colKey] === 'asc') {
        nextDirection = 'desc';
      } else if (sorting[colKey] === 'desc') {
        nextDirection = 'asc';
      }
    }

    if (onSort) {
      onSort({ [colKey]: nextDirection });
    }
  };

  const toggleRowExpansion = (rowKey: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowKey)) {
      newExpanded.delete(rowKey);
    } else {
      newExpanded.add(rowKey);
    }
    setExpandedRows(newExpanded);
  };

  // Column resizing handlers
  const handleResizeStart = useCallback(
    (columnKey: string, e: React.MouseEvent) => {
      if (!enableColumnResizing) return;

      e.preventDefault();
      e.stopPropagation();

      const column = columns.find(c => c.key === columnKey);
      if (!column || !column.isResizable) return;

      const currentWidth = columnWidths[columnKey] || column.defaultWidth || 50;
      resizeStartXRef.current = e.clientX;
      resizeStartWidthRef.current = currentWidth;
      setResizingColumn(columnKey);

      // Prevent text selection during resize
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    },
    [enableColumnResizing, columns, columnWidths]
  );

  const handleResizeMove = useCallback(
    (e: MouseEvent) => {
      if (!resizingColumn) return;

      const column = columns.find(c => c.key === resizingColumn);
      if (!column || !column.isResizable) return;

      const deltaX = e.clientX - resizeStartXRef.current;
      const newWidth = Math.max(resizeStartWidthRef.current + deltaX, 50);
      setColumnWidths(prev => {
        const updated = { ...prev, [resizingColumn]: newWidth };
        if (onColumnWidthChange) {
          onColumnWidthChange(resizingColumn, newWidth);
        }
        return updated;
      });
    },
    [resizingColumn, columns, onColumnWidthChange]
  );

  const handleResizeEnd = useCallback(() => {
    if (!resizingColumn) return;

    setResizingColumn(null);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, [resizingColumn]);

  // Setup global mouse event listeners for resizing
  useEffect(() => {
    if (!resizingColumn) {
      // Cleanup on unmount or when not resizing
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      return;
    }

    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [resizingColumn, handleResizeMove, handleResizeEnd]);

  const tableData = data?.data || [];
  const hasData = tableData.length > 0;
  const showTable = !isLoading && data && hasData;
  const showPagination =
    !hidePagination && !isLoading && data && data.total > 0 && pagination && onPaginationChange;

  const containerClass = useMemo(() => {
    let baseClass = 'overflow-x-auto border border-gray-200 dark:border-gray-700';
    // Only round top corners when pagination is shown at bottom, keep bottom flat for seamless connection with pagination
    if (showPagination) {
      baseClass += ' rounded-t-lg';
    } else {
      baseClass += ' rounded-lg';
    }
    if (tableMaxHeight) {
      baseClass += ` overflow-y-auto relative`;
      if (stickyHeader) {
        baseClass += ' min-h-[700px]';
      }
    }
    return `${baseClass} ${tableContainerClassName}`;
  }, [tableMaxHeight, stickyHeader, tableContainerClassName, showPagination]);

  const tableWrapperStyle = useMemo(() => {
    if (tableMaxHeight) {
      return { maxHeight: tableMaxHeight };
    }
    return undefined;
  }, [tableMaxHeight]);

  const headerBaseClass = useMemo(
    () =>
      stickyHeader
        ? 'bg-xtnd-light dark:bg-xtnd-dark sticky top-0 z-30 shadow-xtnd-md'
        : 'bg-gray-50 dark:bg-gray-700 shadow-xtnd',
    [stickyHeader]
  );

  return (
    <div className="flex flex-col">
      {renderHeader && <div className="mb-6 mt-6 flex-shrink-0">{renderHeader()}</div>}

      {/* Action Section - Above table, aligned to the right */}
      {showTable && renderAction && <div className="flex justify-end mb-2">{renderAction()}</div>}

      {isLoading && (
        <div className="animate-pulse flex-shrink-0">
          <div className="grid grid-cols-1 gap-4 mb-4">
            {Array.from({ length: loadingColumnCount }).map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 dark:bg-xtnd-dark-700 rounded"></div>
            ))}
          </div>
          {Array.from({ length: loadingRowCount }).map((_, i) => (
            <div key={i} className="grid grid-cols-1 gap-4 mb-3">
              {Array.from({ length: loadingColumnCount }).map((_, j) => (
                <div key={j} className="h-6 bg-gray-200 dark:bg-xtnd-dark-700 rounded"></div>
              ))}
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="mb-6 flex-shrink-0">
          <ErrorCard
            title="Error loading data"
            message={error.message || 'An error occurred while loading the data.'}
            onRetry={onRetry}
            statusCode={parseApiErrorUtil(error)?.statusCode}
          />  
        </div>
      )}

      {!isLoading && !error && (!data || !hasData) && (
        <div className="flex-shrink-0">
          <EmptyState
            icon={(emptyState.icon as any) || 'HiUsers'}
            title={emptyState.title || 'No data available'}
            description={
              emptyState.description || 'There are no items matching your current filters.'
            }
            variant="outlined"
            padding="lg"
          />
        </div>
      )}

      {/* Table Container */}
      {showTable && (
        <div className="relative">
          <div className={containerClass} style={tableWrapperStyle}>
            <table className={tableClassName}>
              <thead className={headerBaseClass}>
                <tr>
                  {renderExpandedRow && (
                    <th className="w-10 px-4 py-3 border-b border-r border-gray-200 dark:border-gray-700"></th>
                  )}
                  {columns.map((column, colIndex) => {
                    const alignClass =
                      column.align === 'right'
                        ? 'text-right'
                        : column.align === 'center'
                          ? 'text-center'
                          : 'text-left';
                    const isLastColumn = colIndex === columns.length - 1;
                    const isResizable = enableColumnResizing && column.isResizable;
                    const columnWidth = columnWidths[column.key] || undefined;
                    const headerClass = `${isResizable ? '' : 'whitespace-nowrap'} px-6 py-3 ${alignClass} text-xs font-medium text-xtnd-dark dark:text-xtnd-white uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 ${
                      !isLastColumn ? 'border-r' : ''
                    } ${column.width || ''} ${column.headerClassName || ''} ${isResizable ? 'relative' : ''}`;

                    const headerStyle: React.CSSProperties = {
                      cursor: column.isSortable ? 'pointer' : 'default',
                      ...(columnWidth && {
                        width: `${columnWidth}px`,
                        minWidth: `${columnWidth}px`,
                      }),
                    };

                    return (
                      <th
                        key={column.key}
                        className={headerClass}
                        style={headerStyle}
                        onClick={() => column.isSortable && handleSorting(column.key)}
                      >
                        {column.renderHeader ? column.renderHeader() : column.header}
                        {column.isSortable &&
                          !isEmpty(sorting) &&
                          sorting[column.key] &&
                          (sorting[column.key] === 'asc' ? (
                            <Icon
                              size="xs"
                              name="HiArrowUp"
                              className="inline-block ml-2 h-4 w-4 text-gray-400 text-xtnd-blue"
                            />
                          ) : (
                            <Icon
                              size="xs"
                              name="HiArrowDown"
                              className="inline-block ml-2 h-4 w-4 text-gray-400 text-xtnd-blue"
                            />
                          ))}
                        {column.isSortable && !sorting[column.key] && (
                          <Icon
                            size="xs"
                            name="HiArrowsUpDown"
                            className="inline-block ml-2 h-4 w-4 text-gray-400"
                          />
                        )}
                        {isResizable && (
                          <button
                            type="button"
                            className={`absolute right-0 top-0 bottom-0 w-1 cursor-col-resize transition-colors border-0 bg-transparent p-0 ${
                              resizingColumn === column.key ? 'bg-xtnd-blue' : 'hover:bg-xtnd-blue'
                            }`}
                            onMouseDown={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleResizeStart(column.key, e);
                            }}
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            aria-label={`Resize ${column.key} column`}
                          />
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800">
                {tableData.map((row, index) => {
                  const rowKey = getRowKey(row, index);
                  const isExpanded = expandedRows.has(rowKey);
                  const canExpand = isRowExpandable ? isRowExpandable(row, index) : true;
                  const rowClassName = getRowClassName
                    ? getRowClassName(row, index)
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150';
                  const rowClickHandler = onRowClick
                    ? (e: React.MouseEvent) => {
                        // Don't trigger row click if clicking on expand cell or if resizing
                        if (resizingColumn) return;

                        const target = e.target as HTMLElement;
                        const expandCell = target.closest('td[data-expand-cell="true"]');
                        const resizeHandle = target.closest('[role="separator"]');
                        if (!expandCell && !resizeHandle) {
                          onRowClick(row, index);
                        }
                      }
                    : undefined;

                  return (
                    <Fragment key={rowKey}>
                      <tr
                        className={`${rowClassName} ${onRowClick ? 'cursor-pointer' : ''}`}
                        onClick={rowClickHandler}
                      >
                        {renderExpandedRow && (
                          <td
                            className={`w-14 px-2 py-3 whitespace-nowrap border-b border-r border-gray-200 dark:border-gray-700 ${
                              canExpand
                                ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
                                : ''
                            }`}
                            onClick={canExpand ? e => toggleRowExpansion(rowKey, e) : undefined}
                            role={canExpand ? 'button' : undefined}
                            aria-label={
                              canExpand ? (isExpanded ? 'Collapse row' : 'Expand row') : undefined
                            }
                            data-expand-cell="true"
                          >
                            {canExpand && (
                              <div className="flex items-center justify-center">
                                <Icon
                                  name={isExpanded ? 'HiChevronDown' : 'HiChevronRight'}
                                  className="h-5 w-5 text-gray-400"
                                />
                              </div>
                            )}
                          </td>
                        )}
                        {columns.map((column, colIndex) => {
                          const alignClass =
                            column.align === 'right'
                              ? 'text-right'
                              : column.align === 'center'
                                ? 'text-center'
                                : 'text-left';
                          const isLastColumn = colIndex === columns.length - 1;
                          const cellClass = `px-6 py-4 ${alignClass} border-b border-gray-200 dark:border-gray-700 ${
                            !isLastColumn ? 'border-r' : ''
                          } ${column.className || ''}`;

                          return (
                            <td key={column.key} className={cellClass}>
                              {column.renderCell(row, index)}
                            </td>
                          );
                        })}
                      </tr>

                      {renderExpandedRow && isExpanded && canExpand && (
                        <tr className="bg-gray-50 dark:bg-gray-800">
                          <td
                            colSpan={columns.length + 1}
                            className="px-6 py-4 border-b border-gray-200 dark:border-gray-700"
                          >
                            {renderExpandedRow(row, index)}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
              {renderFooter && <tfoot>{renderFooter()}</tfoot>}
            </table>
          </div>

          {showPagination && pagination && (
            <div className="border-b border-l border-r border-gray-200 dark:border-gray-700 rounded-b-lg overflow-hidden bg-white dark:bg-gray-800">
              <Pagination
                currentPage={pagination.page}
                totalPages={Math.ceil(data.total / pagination.limit)}
                totalRecords={data.total}
                hasNextPage={pagination.page < Math.ceil(data.total / pagination.limit)}
                hasPreviousPage={pagination.page > 1}
                isLoading={isLoading}
                pageSize={pagination.limit}
                pageSizeOptions={pageSizeOptions}
                onPageChange={page => onPaginationChange({ ...pagination, page })}
                onPageSizeChange={pageSize =>
                  onPaginationChange({ ...pagination, page: 1, limit: pageSize })
                }
                showPageSizeSelector={true}
                showRecordCount={true}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
